import { useMemo, useState } from "react";
import styles from "./DatePicker.module.scss";
import Button from "../Button/Button";
import Calendar from "./ui/Calendar";
import DropdownIcon from "../../assets/icons/dropdown-blue-icon.svg?react";
import PrevMonthIcon from "../../assets/images/icons/ui/arrows/arrow-light-blue-left.svg";
import NextMonthIcon from "../../assets/images/icons/ui/arrows/arrow-light-blue-right.svg";
import {
  getCurrentYear,
  getCurrentMonth,
  getCurrentDay,
  getLongMonthNames,
  isRangeFinal,
} from "./utils/index";

// Earliest year available when calendarLimitMonthsAvailable is true
const MIN_AVAILABLE_YEAR = 2025;
const MIN_AVAILABLE_MONTH_IN_LIMIT_YEAR = 5; // June (0-indexed)

export type DateRange = [Date | undefined, Date | undefined];

export interface DatePickerProps {
  /**
   * The locale date format
   */
  locale?: string;
  /**
   * The default range of the DatePicker
   */
  defaultRange?: DateRange;
  /**
   * To  to ensure the value is after a specific date.
   */
  min?: Date;
  /**
   * To  to ensure the value is before a specific date.
   */
  max?: Date;
  /**
   * Handler called when the DatePicker's value changes.
   */
  onApply?: (date: DateRange) => void;
  /**
   * Handler called when the DatePicker's range changes (before applying).
   */
  onChange?: (range: DateRange) => void;
  /**
   * Handler called when the DatePicker's value is deleted.
   */
  onDelete?: () => void;
  /**
   * Handler called when the DatePicker's has a back arrow.
   */
  onBack?: () => void;
  /**
   * To set default value when onDelete handler is called.
   */
  restoreDefaultOnDelete?: boolean;
  /**
   * The variant of the DatePicker's value to choose
   */
  defaultCalendarVariant?: "DAY" | "MONTH" | "DATE";
  /**
   * To style dropdowns in DatePicker
   */
  dropdownClassName?: string;
  /**
   * To style menu in DatePicker
   */
  menuClassName?: string;
  /**
   * To style header in DatePicker
   */
  headerClassName?: string;
  /**
   * To style calendar in DatePicker
   */
  calendarClassName?: string;
  /**
   * To style footer in DatePicker
   */
  footerClassName?: string;
  resetActionLabel?: boolean;
  /**
   * Minimum number of days required in the range (inclusive)
   */
  minDays?: number;
  /** Component before the footer */
  calendarPreFooter?: React.ReactNode;
  /** Calendar with a limit on available months */
  calendarLimitMonthsAvailable?: boolean;
}

interface InlinePickerProps {
  isOpen: boolean;
  onToggle: (e: React.MouseEvent) => void;
  displayValue: string;
  options: { label: string; key: string; isActive: boolean }[];
  onSelect: (e: React.MouseEvent, key: string) => void;
}

const InlinePicker = ({ isOpen, onToggle, displayValue, options, onSelect }: InlinePickerProps) => {
  const { pickerWrapper, dropdownButton, dropdownText, pickerList, pickerItem, pickerItemActive } =
    styles;
  return (
    <div className={pickerWrapper}>
      <button type="button" className={dropdownButton} onClick={onToggle}>
        <span className={dropdownText}>{displayValue}</span>
        <DropdownIcon />
      </button>
      {isOpen && (
        <div className={pickerList}>
          {options.map(({ label, key, isActive }) => (
            <button
              key={key}
              type="button"
              className={`${pickerItem} ${isActive ? pickerItemActive : ""}`}
              onClick={(e) => onSelect(e, key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 *  Allow users to select a date or a range of dates
 */
const DatePicker = (props: DatePickerProps) => {
  const {
    locale = "es-AR",
    defaultRange,
    min = new Date(getCurrentYear() - 4, getCurrentMonth(), getCurrentDay()),
    max = new Date(getCurrentYear() + 2, getCurrentMonth(), getCurrentDay()),
    onApply,
    onChange,
    onDelete,
    restoreDefaultOnDelete,
    defaultCalendarVariant = "DAY",
    menuClassName,
    headerClassName,
    calendarClassName,
    footerClassName,
    minDays,
    calendarPreFooter,
    calendarLimitMonthsAvailable = false,
  } = props;

  const { menu: menuStyle, header, iconButton, iconButtonDisabled } = styles;

  const [openPicker, setOpenPicker] = useState<"year" | "month" | null>(null);

  const setInitialRange = () => {
    if (defaultRange) {
      if (defaultCalendarVariant === "MONTH") {
        defaultRange[0]?.setDate(1);
        defaultRange[1]?.setDate(1);
      }
      return defaultRange;
    } else return [undefined, undefined] as DateRange;
  };

  const initialRangeState = setInitialRange();
  const [range, setRange] = useState<DateRange>(initialRangeState);
  const initialDateToView = initialRangeState[1] || initialRangeState[0];
  const [calendarYear, setCalendarYear] = useState(
    initialDateToView
      ? initialDateToView.getFullYear()
      : Math.max(getCurrentYear(), min.getFullYear()),
  );
  const [calendarMonth, setCalendarMonth] = useState(
    initialDateToView
      ? initialDateToView.getMonth()
      : calendarLimitMonthsAvailable
        ? Math.min(getCurrentMonth(), 11)
        : Math.max(getCurrentMonth(), min.getMonth()),
  );

  const calendarVariant = defaultCalendarVariant;

  const isPrevDisabled =
    calendarYear === min.getFullYear() && calendarMonth === min.getMonth();
  const isNextDisabled =
    calendarYear === max.getFullYear() && calendarMonth === max.getMonth();

  const prevMonth = () => {
    if (isPrevDisabled) return;
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const nextMonth = () => {
    if (isNextDisabled) return;
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const validateMinDays = (dateRange: DateRange): boolean => {
    if (!minDays || !dateRange[0] || !dateRange[1]) return true;

    const startDate = new Date(dateRange[0]);
    const endDate = new Date(dateRange[1]);
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;

    return diffInDays >= minDays;
  };

  const handleApply = () => {
    if (!isRangeFinal(range)) return;
    if (onApply) onApply(range);
  };

  const handleRangeChange = (value: React.SetStateAction<DateRange>) => {
    const newRange = typeof value === "function" ? value(range) : value;

    if (newRange[0] && newRange[1]) {
      if (validateMinDays(newRange)) {
        setRange(newRange);
        onChange?.(newRange);
      }
    } else {
      setRange(newRange);
      onChange?.(newRange);
    }
  };

  const yearOptions = useMemo(
    () =>
      calendarLimitMonthsAvailable
        ? Array.from(Array(getCurrentYear() + 1 - MIN_AVAILABLE_YEAR).keys()).map((i) =>
            (MIN_AVAILABLE_YEAR + i).toString(),
          )
        : Array.from(Array(max.getFullYear() + 1 - min.getFullYear()).keys()).map((_, i) =>
            (min.getFullYear() + i).toString(),
          ),
    [calendarLimitMonthsAvailable, max, min],
  );

  const monthOptions = useMemo(
    () =>
      getLongMonthNames(locale)
        .map((name, i) => ({ label: name, value: i }))
        .filter(({ value: i }) => {
          if (calendarYear === min.getFullYear() && i < min.getMonth()) return false;
          if (calendarYear === max.getFullYear() && i > max.getMonth()) return false;
          if (!calendarLimitMonthsAvailable) return true;
          if (calendarYear === MIN_AVAILABLE_YEAR && i < MIN_AVAILABLE_MONTH_IN_LIMIT_YEAR)
            return false;
          if (calendarYear === getCurrentYear() && i > getCurrentMonth()) return false;
          return true;
        }),
    [locale, calendarLimitMonthsAvailable, calendarYear, min, max],
  );

  const handleYearSelect = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    const newYear = Number.parseInt(key);
    setCalendarYear(newYear);
    if (calendarLimitMonthsAvailable) {
      if (newYear === MIN_AVAILABLE_YEAR && calendarMonth < MIN_AVAILABLE_MONTH_IN_LIMIT_YEAR)
        setCalendarMonth(MIN_AVAILABLE_MONTH_IN_LIMIT_YEAR);
      else if (newYear === getCurrentYear() && calendarMonth > getCurrentMonth())
        setCalendarMonth(getCurrentMonth());
    }
    setOpenPicker(null);
  };

  const handleMonthSelect = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setCalendarMonth(Number.parseInt(key));
    setOpenPicker(null);
  };

  return (
    <div className={`${menuStyle} ${menuClassName}`}>
      <div className={`${header} ${headerClassName}`}>
        <div className="flex gap-1 flex-grow items-center">
          {["DAY", "DATE"].includes(calendarVariant) && (
            <InlinePicker
              isOpen={openPicker === "month"}
              onToggle={(e) => {
                e.stopPropagation();
                setOpenPicker((o) => (o === "month" ? null : "month"));
              }}
              displayValue={getLongMonthNames(locale)[calendarMonth]}
              options={monthOptions.map(({ label, value }) => ({
                label,
                key: value.toString(),
                isActive: value === calendarMonth,
              }))}
              onSelect={handleMonthSelect}
            />
          )}
          <InlinePicker
            isOpen={openPicker === "year"}
            onToggle={(e) => {
              e.stopPropagation();
              setOpenPicker((o) => (o === "year" ? null : "year"));
            }}
            displayValue={calendarYear.toString()}
            options={yearOptions.map((year) => ({
              label: year,
              key: year,
              isActive: year === calendarYear.toString(),
            }))}
            onSelect={handleYearSelect}
          />
        </div>
        <div className="flex gap-1">
          <div
            className={`${iconButton} ${isPrevDisabled ? iconButtonDisabled : ""}`}
            onClick={prevMonth}
          >
            <img src={PrevMonthIcon} />
          </div>
          <div
            className={`${iconButton} ${isNextDisabled ? iconButtonDisabled : ""}`}
            onClick={nextMonth}
          >
            <img src={NextMonthIcon} />
          </div>
        </div>
      </div>

      <Calendar
        variant={calendarVariant}
        locale={locale}
        calendarYear={calendarYear}
        calendarMonth={calendarMonth}
        min={min}
        max={max}
        range={range}
        setRange={handleRangeChange}
        className={calendarClassName}
      />

      {calendarPreFooter}

      <div
        className={`${footerClassName ?? ""} flex flex-col gap-2 flex-wrap w-full justify-evenly`}
      >
        {(defaultCalendarVariant !== "DATE" || onDelete) && (
          <Button
            text={props.resetActionLabel ? "Restablecer" : "Borrar"}
            variant="secondary"
            onPress={() => {
              setRange(
                restoreDefaultOnDelete && defaultRange
                  ? defaultRange
                  : [undefined, undefined],
              );
              onDelete && onDelete();
            }}
          />
        )}

        <Button
          text="Aplicar"
          variant="primary"
          isDisabled={!isRangeFinal(range)}
          onPress={handleApply}
        />
      </div>
    </div>
  );
};

export default DatePicker;
